import { useEffect, useState, type ReactNode } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  Calendar,
  Headphones,
  Home,
  ImageIcon,
  LogOut,
  Menu,
  Users,
  X
} from 'lucide-react'
import { GlobalSearch } from '../GlobalSearch/GlobalSearch'
import { InstallBanner } from '../ui/InstallBanner'
import { TivyLogo } from '../ui/TivyLogo'
import { useAuthStore } from '../../stores/authStore'
import type { UserRole } from '../../types/api'
import styles from './ConsoleLayout.module.css'

interface SidebarItem {
  to: string
  label: string
  icon: ReactNode
  /** Si true, NavLink matchea path exacto. Para "Inicio" con `to="/app/admin"` que no se active en `/app/admin/clientes`. */
  end?: boolean
}

const ITEMS_BY_ROLE: Record<UserRole, SidebarItem[]> = {
  super_admin: [
    { to: '/app/admin', label: 'Inicio', icon: <Home size={18} />, end: true },
    { to: '/app/admin/clientes', label: 'Clientes', icon: <Users size={18} /> },
    { to: '/app/admin/eventos', label: 'Eventos', icon: <Calendar size={18} /> },
    { to: '/app/admin/operadores', label: 'Operadores', icon: <Headphones size={18} /> },
    { to: '/app/admin/galerias', label: 'Galerías', icon: <ImageIcon size={18} /> }
  ],
  client: [
    { to: '/app/cliente', label: 'Inicio', icon: <Home size={18} />, end: true },
    { to: '/app/cliente/eventos', label: 'Eventos', icon: <Calendar size={18} /> },
    { to: '/app/cliente/operadores', label: 'Operadores', icon: <Headphones size={18} /> },
    { to: '/app/cliente/galerias', label: 'Galerías', icon: <ImageIcon size={18} /> }
  ],
  operator: [
    { to: '/app/operador', label: 'Inicio', icon: <Home size={18} />, end: true },
    { to: '/app/operador/eventos', label: 'Mis eventos', icon: <Calendar size={18} /> }
  ]
}

export function ConsoleLayout() {
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const navigate = useNavigate()
  const location = useLocation()

  // Mobile drawer state. Cerrado por default. Auto-cierra al navegar
  // (location.pathname change) — mobile UX típico.
  const [drawerOpen, setDrawerOpen] = useState(false)

  useEffect(() => {
    setDrawerOpen(false)
  }, [location.pathname])

  if (!user) return null

  const items = ITEMS_BY_ROLE[user.role]

  const handleLogout = () => {
    logout()
    navigate('/app/login', { replace: true })
  }

  const roleLabel =
    user.role === 'super_admin' ? 'Super admin' : user.role === 'client' ? 'Cliente' : 'Operador'

  return (
    <div className={styles.layout} data-console="true">
      {/* Backdrop del drawer mobile — visible solo cuando drawerOpen=true */}
      {drawerOpen && (
        <div
          className={styles.drawerBackdrop}
          onClick={() => setDrawerOpen(false)}
          aria-hidden
        />
      )}

      <aside
        className={styles.sidebar}
        data-open={drawerOpen ? 'true' : 'false'}
      >
        <div className={styles.brand}>
          <TivyLogo variant="claro" className={styles.brandLogo} />
          <span className={styles.brandRole}>{roleLabel}</span>
        </div>
        {/*
          F024 §4.6: GlobalSearch dentro del drawer mobile. En desktop
          esta instancia queda oculta (el sidebar siempre está visible y
          el GlobalSearch del header se ve); en mobile el header sin
          GlobalSearch + drawer con GlobalSearch dan acceso unificado.
        */}
        {user.role === 'super_admin' && (
          <div className={styles.drawerSearchSlot}>
            <GlobalSearch />
          </div>
        )}
        <nav className={styles.nav}>
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end ?? false}
              className={({ isActive }) =>
                `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
              }
              style={{ color: 'var(--color-text)' }}
            >
              <span className={styles.navIcon} style={{ color: 'var(--color-primary)' }}>
                {item.icon}
              </span>
              {item.label}
            </NavLink>
          ))}
        </nav>
        {/* Botón cerrar drawer — visible solo en mobile (CSS-controlled) */}
        <button
          type="button"
          className={styles.drawerCloseBtn}
          onClick={() => setDrawerOpen(false)}
          aria-label="Cerrar menú"
        >
          <X size={18} />
        </button>
      </aside>
      <div className={styles.main}>
        <header className={styles.header}>
          {/* Hamburger — visible solo en mobile via CSS */}
          <button
            type="button"
            className={styles.menuBtn}
            onClick={() => setDrawerOpen(true)}
            aria-label="Abrir menú"
          >
            <Menu size={20} />
          </button>
          {user.role === 'super_admin' && (
            <div className={styles.searchSlot}>
              <GlobalSearch />
            </div>
          )}
          <div className={styles.userBlock}>
            <span className={styles.userName}>{user.name}</span>
            <span className={styles.userEmail}>{user.email}</span>
          </div>
          <button type="button" className={styles.logout} onClick={handleLogout}>
            <LogOut size={18} style={{ color: 'var(--color-primary)' }} />
            Salir
          </button>
        </header>
        <main className={styles.content}>
          <Outlet />
        </main>
      </div>
      <InstallBanner label="la consola de Tivy" />
    </div>
  )
}
